import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  CARD_SECTION_TITLE_CLASS,
} from '@safetyhub/ui';
import { useAuthStore } from '../../stores/auth';
import { useToastStore } from '../../stores/toast';
import { tokenManager } from '../../lib/api';
import DeleteAccountModal from '../../components/modals/DeleteAccountModal';
import DataExportCard from '../../components/settings/DataExportCard';
import { getAppVersion } from '../../lib/version';

export default function AccountSettings() {
  const navigate = useNavigate();
  const { logout } = useAuthStore();
  const { showToast } = useToastStore();

  // 회원 탈퇴 모달 상태
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // 회원 탈퇴 성공
  const handleDeleteSuccess = async () => {
    showToast('회원 탈퇴가 완료되었습니다. 30일 이내에 로그인하면 복구할 수 있습니다.', 'info');
    tokenManager.clearTokens();
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div>
        <h1 className="text-[26px] font-extrabold tracking-tight text-ink-900">설정</h1>
        <p className="mt-1 text-[15px] font-medium text-ink-500">
          계정 및 데이터 관련 설정을 관리합니다.
        </p>
      </div>

      {/* 넓은 화면에서는 두 관리 카드를 나란히, 위험 구역은 시선 흐름 마지막에 전폭으로 둔다. */}
      <div className="space-y-6 lg:grid lg:grid-cols-2 lg:gap-6 lg:space-y-0">
        {/* 데이터 관리 — 전체 데이터 내보내기 (결정 기록 2026-08-22).
            2026-08-22에 죽은 버튼(POST /data-export/user 404)을 걷어냈다가, 그 버튼이 가리고
            있던 공백(**비OWNER는 탈퇴하지 않고서는 자기 데이터를 못 받는다**)을 확인하고
            라우트를 만들어 다시 붙였다. */}
        <DataExportCard />

        {/* 앱 정보 */}
        <Card padding="xl" radius="lg" elevation="md">
          <h3 className={`${CARD_SECTION_TITLE_CLASS} mb-4`}>앱 정보</h3>
          <AppVersionInfo />
        </Card>

        {/* 위험 구역 */}
        <div className="bg-white shadow-card rounded-card-lg border border-rose-200 lg:col-span-2">
          <div className="px-6 py-4 border-b border-rose-200 bg-rose-50">
            <h3 className="text-[15px] font-extrabold tracking-tight text-rose-800">위험 구역</h3>
          </div>
          <div className="px-6 py-4">
            {/* 카드 제목(15px extrabold) 한 단계 아래 소제목 — rc 소제목 규약(14.5/700) 준용 */}
            <h4 className="text-[14.5px] font-bold tracking-tight text-ink-900 mb-2">회원 탈퇴</h4>
            <p className="text-sm text-ink-600 mb-2">
              탈퇴하면 모든 조직 멤버십이 해제되고, 30일 후 계정이 완전히 삭제됩니다.
            </p>
            <p className="text-sm text-ink-500 mb-4">
              30일 이내에 동일한 계정으로 로그인하면 계정을 복구할 수 있습니다.
            </p>
            <Button variant="danger" onClick={() => setIsDeleteModalOpen(true)}>
              회원 탈퇴
            </Button>
          </div>
        </div>
      </div>

      {/* 회원 탈퇴 모달 */}
      <DeleteAccountModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onSuccess={handleDeleteSuccess}
      />
    </div>
  );
}

/**
 * 앱 버전 정보 컴포넌트
 */
function AppVersionInfo() {
  const version = getAppVersion();
  const buildDate = new Date(version.buildDate);
  const formattedDate = buildDate.toLocaleString('ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <span className="text-ink-500">버전</span>
          <p className="font-medium text-ink-900">{version.fullVersion}</p>
        </div>
        <div>
          <span className="text-ink-500">빌드 일시</span>
          <p className="font-medium text-ink-900">{formattedDate}</p>
        </div>
        <div>
          <span className="text-ink-500">커밋</span>
          <p className="font-mono text-ink-900">{version.commitHash}</p>
        </div>
      </div>
    </div>
  );
}
